import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { VictoryLabel } from 'victory';

import { Icon } from '~/components';
import { LineGraph } from '~/components/LineGraph/LineGraph';
import type { FixesHistValue } from '~/modules/label-perf/types/label-perf';
import { LegendColor, LegendName } from '~/modules/label-perf/types/label-perf';

import { ContentTooltip } from './ChartTooltip';
import { ErrorDisplay } from './ErrorDisplay';

export interface ChartItem extends FixesHistValue {
  __original: Omit<FixesHistValue, 'name'>;
}

interface EnrichedPointData {
  seriesName: LegendName;
  rawValue: number;
  seriesColor: LegendColor;
  x: string | number;
  y: string | number;
  delta?: number;
  label?: string;
  date?: string;
}

export interface ChartProps {
  className?: string;
  data: FixesHistValue[];
  isLoading: boolean;
  isError: boolean;
}

const Chart = ({
  className,
  data,
  isLoading,
  isError,
}: ChartProps): JSX.Element => {
  const { t } = useTranslation('label-perf');
  const ref = useRef<HTMLDivElement>(null);

  const series = useMemo<EnrichedPointData[][]>(() => {
    const need: EnrichedPointData[] = [];
    const review: EnrichedPointData[] = [];
    const fixed: EnrichedPointData[] = [];

    data.forEach((item) => {
      const total = item.needFixes + item.inReview + item.fixed;
      const pct = (n: number) =>
        total ? Math.round((n / total) * 10000) / 100 : 0;

      const xValue = item.name;
      const yNeed = pct(item.needFixes);
      const yReview = pct(item.inReview);
      const yFixed = pct(item.fixed);

      need.push({
        x: xValue,
        y: yNeed,
        date: xValue,
        seriesName: LegendName.NeedsReview,
        rawValue: item.needFixes,
        seriesColor: LegendColor.NeedsReview,
      } as EnrichedPointData);

      review.push({
        x: xValue,
        y: yReview,
        date: xValue,
        seriesName: LegendName.InReview,
        rawValue: item.inReview,
        seriesColor: LegendColor.InReview,
      } as EnrichedPointData);

      fixed.push({
        x: xValue,
        y: yFixed,
        date: xValue,
        seriesName: LegendName.FixedTracks,
        rawValue: item.fixed,
        seriesColor: LegendColor.FixedTracks,
      } as EnrichedPointData);
    });

    return [need, review, fixed];
  }, [data]);

  return (
    <div
      data-testid="chart-container"
      className={className}
      ref={ref}
      style={{ position: 'relative' }}
    >
      {/* loading & error overlay */}
      {data.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          {isLoading ? (
            <Icon
              type="spinner"
              variant="solid"
              size="lg"
              className="fa-spin text-body-xl"
              data-testid="loader"
            />
          ) : null}
          {isError ? (
            <ErrorDisplay
              title={t('error.failedLoading')}
              description={t('error.refresh')}
              className="z-10"
            />
          ) : null}
        </div>
      )}

      <div className="mr-xl">
        <LineGraph
          data={series}
          lineColors={[
            LegendColor.NeedsReview,
            LegendColor.InReview,
            LegendColor.FixedTracks,
          ]}
          containerClassName="w-full"
          tooltip={<ContentTooltip />}
          tooltipPadding={12}
          tooltipOffset={[5, -20]}
          xAxisProps={{
            style: {
              axisLabel: { padding: 10 },
              tickLabels: {
                fontSize: 3.5,
                angle: 0,
                textAnchor: 'middle',
                fill: 'white',
              },
            },
            tickValues: series[0].map((d) => d.x),
            tickFormat: (x: string) => x,
            tickLabelComponent: <VictoryLabel textAnchor="start" />,
            offsetX: 20,
          }}
          yAxisProps={{
            orientation: 'right',
            //
            style: {
              tickLabels: { fontSize: 3.5, padding: 4, fill: 'white' },
            },
            tickFormat: (v: number) => `${v}%`,
            domain: [0, Math.floor(100 * 1)],
            tickValues: [0, 25, 50, 75, 100],
          }}
        />
      </div>
    </div>
  );
};

Chart.displayName = 'Chart';
export { Chart };
